import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { knowledgeService, tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Trash2 } from "lucide-react";

function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-foreground mb-1">{label}</label>{children}</div>;
}

export default function TagsPage() {
  const { hasPermission, user } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", color: "#64748b", tenantId: "" });
  const [saving, setSaving] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [tenantFilter, setTenantFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await knowledgeService.listTags({ tenantId: isSuper ? (tenantFilter || undefined) : undefined }); setRows(d.items || []); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [isSuper, tenantFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {}); }, [isSuper]);

  const canManage = hasPermission("KNOWLEDGE.MANAGE_TAG");

  const openNew = () => { setEditing(null); setForm({ name: "", color: "#64748b", tenantId: tenantFilter || "" }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (isSuper && !editing && !form.tenantId) return toast.error("Select a tenant");
    setSaving(true);
    try {
      const payload = { name: form.name, color: form.color };
      if (editing) await knowledgeService.updateTag(editing.id, payload);
      else { if (isSuper) payload.tenantId = form.tenantId; await knowledgeService.createTag(payload); }
      toast.success(editing ? "Tag updated" : "Tag created");
      setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete tag "${r.name}"?`)) return;
    try { await knowledgeService.removeTag(r.id); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="tags-page">
      <PageHeader title="Tags" subtitle="Lightweight labels to cross-link knowledge"
        actions={canManage && (
          <button onClick={openNew} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90" data-testid="tag-create-btn">
            <Plus className="h-4 w-4" /> New Tag
          </button>
        )}
      />
      {isSuper && (
        <div className="mb-4">
          <select value={tenantFilter} onChange={(e) => setTenantFilter(e.target.value)} className="bg-card border border-border rounded-lg px-3 py-2 text-sm text-foreground" data-testid="tag-tenant-filter">
            <option value="">All tenants</option>
            {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
          </select>
        </div>
      )}
      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div> : (
        <div className="flex flex-wrap gap-2" data-testid="tags-list">
          {rows.length === 0 && <p className="text-sm text-muted-foreground py-10 w-full text-center">No tags yet</p>}
          {rows.map((r) => (
            <div key={r.id} className="inline-flex items-center gap-2 bg-card rounded-full border border-border pl-3 pr-1.5 py-1.5" data-testid={`tag-chip-${r.id}`}>
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: r.color }} />
              <span className="text-sm text-foreground">{r.name}</span>
              {canManage && (
                <span className="flex items-center">
                  <button onClick={() => openEdit(r)} className="p-1 rounded-md hover:bg-muted text-muted-foreground" data-testid={`tag-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => remove(r)} className="p-1 rounded-md hover:bg-destructive/10 text-destructive" data-testid={`tag-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-sm p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="tag-dialog">
            <h3 className="text-lg font-bold mb-4">{editing ? "Edit Tag" : "New Tag"}</h3>
            <div className="space-y-3.5">
              {isSuper && !editing && (
                <Field label="Tenant *">
                  <select value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value })} className="ciq-input" data-testid="tag-tenant">
                    <option value="">Select tenant…</option>
                    {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
                  </select>
                </Field>
              )}
              <Field label="Name *"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ciq-input" data-testid="tag-name" /></Field>
              <Field label="Color"><input type="color" value={form.color || "#64748b"} onChange={(e) => setForm({ ...form, color: e.target.value })} className="ciq-input h-10 p-1" data-testid="tag-color" /></Field>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="tag-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Save" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
